import React, { useCallback, useMemo, useState } from 'react';
import MarkdownRenderer from './MicroLearning/MarkdownRenderer';
import './PassagePanel.css';

/**
 * The passage, rule set or chart caption a question depends on — shown as its
 * own thing, above the question, never merged into it.
 *
 * ## Why it opens expanded
 *
 * The obvious design is to collapse it by default and save the scroll. The
 * content argues against it. Across the 388 UAT questions that carry a
 * passage the median is 152 characters — about two lines. Hiding two lines
 * behind a tap costs the student more than it saves them, and it makes that
 * tap mandatory on every passage question, which is a tax rather than a
 * choice.
 *
 * It is worse than neutral for two of the three kinds. In Analytical
 * Reasoning the "passage" IS the rules — "Which workshop must be first?" is
 * not a question without them. And in Reading Comprehension, a collapsed
 * default quietly invites answering before reading, which is the exact habit
 * the real paper punishes. On the paper the passage is simply there.
 *
 * So: open by default, and solve the actual problem — a long passage pushing
 * the question off a phone screen — with a height cap and internal scroll
 * rather than by hiding it.
 *
 * ## Why the toggle still exists
 *
 * 56 of the passages are shared by five questions each. By question three the
 * student has read it and mostly wants the question. So the control is real,
 * and the choice STICKS across the group: collapse it on question two and it
 * stays collapsed through question five, one tap to bring it back. That is
 * where collapsing genuinely helps, and it is the student's call rather than
 * a default imposed on the first read.
 */

export type PassagePanelProps = {
  text?: string | null;
  /** Kept for call-site uniformity; no longer used for translation. */
  cacheKey?: string;
  /** Kept so the three call sites stay uniform; unused since the translate
   *  control was removed from the passage — a passage is the exam's own
   *  wording, and offering to rewrite it is offering the wrong help. */
  surface?: string;
  trackProps?: Record<string, any>;
  /** "Passage · question 2 of 5" when the group size is known. */
  position?: { index: number; total: number } | null;
  /** Overrides the heading when the block is not prose. */
  label?: string;
};

/** Long enough that it would push the question off a phone screen. */
const TALL_CHARS = 600;

/** Stable per-passage key, so the collapse choice follows the passage across
 *  every question in its group rather than resetting on each one. */
const keyFor = (text: string): string => {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) {
    h = (h << 5) - h + text.charCodeAt(i);
    h |= 0;
  }
  return `lomi_passage_open:${h}`;
};

const PassagePanel: React.FC<PassagePanelProps> = ({
  text,
  position,
  label,
}) => {
  const storageKey = useMemo(() => (text ? keyFor(text) : ''), [text]);

  const [open, setOpen] = useState<boolean>(() => {
    if (!storageKey) return true;
    try {
      // Absent means never collapsed, which is the default state.
      return sessionStorage.getItem(storageKey) !== '0';
    } catch {
      return true;
    }
  });

  const toggle = useCallback(() => {
    setOpen((wasOpen) => {
      const next = !wasOpen;
      try {
        sessionStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        /* storage blocked — the choice just does not persist */
      }
      return next;
    });
  }, [storageKey]);

  if (!text) return null;

  const tall = text.length > TALL_CHARS;
  const heading = label || 'Passage';

  return (
    <section className={`passagep${open ? '' : ' passagep--closed'}`}>
      <header className="passagep__bar">
        <h3 className="passagep__title">
          {heading}
          {position && position.total > 1 && (
            <span className="passagep__of">
              {' '}· question {position.index} of {position.total}
            </span>
          )}
        </h3>
        <button
          type="button"
          className="passagep__toggle"
          onClick={toggle}
          aria-expanded={open}
        >
          {open ? 'Hide' : 'Show'}
        </button>
      </header>

      {open && (
        <div className={`passagep__body${tall ? ' passagep__body--tall' : ''}`}>
          <MarkdownRenderer className="passagep__md">{text}</MarkdownRenderer>
        </div>
      )}
    </section>
  );
};

export default PassagePanel;
